import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { AppRole, UserProfile } from '@/lib/auth/types';

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  role: AppRole | null;
  loading: boolean;
  bootstrapError: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    role: null,
    loading: true,
    bootstrapError: null,
  });

  const bootstrapUser = useCallback(async (userId: string, email: string, fullName?: string, requestedRole?: AppRole) => {
    try {
      // Call the bootstrap function to ensure profile, role, and client assignment exist
      const { data, error } = await supabase.rpc('bootstrap_user_if_needed', {
        _user_id: userId,
        _email: email,
        _full_name: fullName || null,
        _role: requestedRole || 'surgeon'
      });

      if (error) {
        console.error('Bootstrap error:', error);
        // Don't throw - the trigger should have handled initial creation
      } else if (data) {
        console.log('Bootstrap result:', data);
      }
    } catch (error) {
      console.error('Bootstrap function error:', error);
    }
  }, []);

  const fallbackFromMetadata = useCallback((userId: string, userEmail?: string, userMetadata?: Record<string, unknown>) => {
    // Fallback path when the profiles / user_roles tables are missing in the
    // current Supabase project (e.g., brand-new project without migrations
    // applied, or recovery mode). Pull what we can from the JWT metadata so
    // the session is usable instead of being stuck on a blank auth screen.
    const roleFromMeta =
      (userMetadata?.role as AppRole | undefined) ||
      (userMetadata?.requested_role as AppRole | undefined) ||
      'surgeon';
    const nameFromMeta =
      (userMetadata?.full_name as string | undefined) ||
      (userMetadata?.display_name as string | undefined) ||
      (userMetadata?.name as string | undefined) ||
      userEmail ||
      'User';
    const now = new Date().toISOString();
    setState(prev => ({
      ...prev,
      profile: {
        id: userId,
        user_id: userId,
        full_name: nameFromMeta,
        email: userEmail || '',
        created_at: now,
        updated_at: now,
      },
      role: roleFromMeta,
      loading: false,
      bootstrapError: null,
    }));
  }, []);

  const fetchUserData = useCallback(async (userId: string, userEmail?: string, userMetadata?: Record<string, unknown>) => {
    try {
      // First, try to fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      // Graceful fallback: if the profiles table doesn't exist in this
      // Supabase project, synthesize a profile from user_metadata so the
      // session is still usable. PGRST205 = schema cache miss (table not found).
      if (profileError && (profileError as { code?: string }).code === 'PGRST205') {
        console.warn('profiles table not found; falling back to user_metadata');
        fallbackFromMetadata(userId, userEmail, userMetadata);
        return;
      }

      // If no profile exists, bootstrap the user
      if (!profileData && !profileError) {
        console.log('No profile found, bootstrapping user...');
        await bootstrapUser(
          userId, 
          userEmail || '', 
          userMetadata?.full_name as string || userMetadata?.name as string,
          userMetadata?.requested_role as AppRole
        );
        
        // Retry fetching profile after bootstrap
        const { data: retriedProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (!retriedProfile) {
          setState(prev => ({
            ...prev,
            bootstrapError: 'Unable to create user profile. Please contact support.',
            loading: false,
          }));
          return;
        }
      }

      // Fetch role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      // Graceful fallback: user_roles table missing in this Supabase project.
      if (roleError && (roleError as { code?: string }).code === 'PGRST205') {
        console.warn('user_roles table not found; falling back to user_metadata');
        fallbackFromMetadata(userId, userEmail, userMetadata);
        return;
      }

      // If no role exists, the bootstrap should have created it
      if (!roleData && !roleError) {
        console.log('No role found, attempting bootstrap...');
        await bootstrapUser(
          userId, 
          userEmail || '',
          userMetadata?.full_name as string,
          userMetadata?.requested_role as AppRole
        );
        
        // Retry fetching role
        const { data: retriedRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle();

        if (!retriedRole) {
          setState(prev => ({
            ...prev,
            bootstrapError: 'Unable to assign user role. Please contact support.',
            loading: false,
          }));
          return;
        }
      }

      // Re-fetch all data after any bootstrapping
      const [finalProfile, finalRole] = await Promise.all([
        supabase.from('profiles').select('*').eq('user_id', userId).maybeSingle(),
        supabase.from('user_roles').select('role').eq('user_id', userId).maybeSingle()
      ]);

      // If the role is still missing but the tables exist, fall back to
      // user_metadata rather than leaving the user stuck.
      const resolvedRole =
        (finalRole.data?.role as AppRole | null) ||
        ((userMetadata?.role as AppRole | undefined) ??
          (userMetadata?.requested_role as AppRole | undefined) ??
          null);

      setState(prev => ({
        ...prev,
        profile: finalProfile.data as UserProfile | null,
        role: resolvedRole,
        loading: false,
        bootstrapError: null,
      }));
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Last-resort fallback: synthesize from metadata so sign-in isn't a dead-end.
      fallbackFromMetadata(userId, userEmail, userMetadata);
    }
  }, [bootstrapUser, fallbackFromMetadata]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event);
        
        setState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          bootstrapError: null,
        }));

        // Defer profile/role fetch with setTimeout to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserData(
              session.user.id, 
              session.user.email,
              session.user.user_metadata
            );
          }, 0);
        } else {
          setState(prev => ({
            ...prev,
            profile: null,
            role: null,
            loading: false,
            bootstrapError: null,
          }));
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
      }));

      if (session?.user) {
        fetchUserData(
          session.user.id,
          session.user.email,
          session.user.user_metadata
        );
      } else {
        setState(prev => ({ ...prev, loading: false }));
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchUserData]);

  const signIn = async (email: string, password: string) => {
    setState(prev => ({ ...prev, bootstrapError: null }));
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string, role: AppRole) => {
    // Use all common redirect URLs for email verification
    const origin = window.location.origin;
    const redirectUrl = origin.includes('localhost') 
      ? origin 
      : `${origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          requested_role: role,
        },
      },
    });

    if (error) return { error, user: null };

    // The database trigger will handle profile and role creation
    // No need to manually insert here anymore

    return { error: null, user: data.user };
  };

  const signOut = async () => {
    // Log audit event before signing out (best-effort)
    try {
      const { logUserLogout } = await import('@/lib/security/auditLogger');
      await logUserLogout();
    } catch (e) {
      console.warn('Audit log on logout failed:', e);
    }

    const { error } = await supabase.auth.signOut();
    if (!error) {
      // Clear all browser storage for PHI/session security
      localStorage.clear();
      sessionStorage.clear();

      setState({
        user: null,
        session: null,
        profile: null,
        role: null,
        loading: false,
        bootstrapError: null,
      });
    }
    return { error };
  };

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    isAuthenticated: !!state.session,
    isSurgeon: state.role === 'surgeon',
    isLinkOps: state.role === 'link_ops' || state.role === 'admin',
    isAdmin: state.role === 'admin',
  };
}
